'use client';

import { useState } from 'react';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';
import Link from 'next/link';

interface FAQItem {
  category: string;
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  // Booking & Payments
  {
    category: 'Booking & Payments',
    question: 'How do I book a VIP package?',
    answer:
      'Browse our events page, select your desired concert, choose a VIP package, and complete the booking form. Payment is made securely using USDT cryptocurrency. You\'ll receive instant confirmation via email.',
  },
  {
    category: 'Booking & Payments',
    question: 'What payment methods do you accept?',
    answer:
      'We accept USDT (Tether) cryptocurrency on ERC-20, TRC-20, and BEP-20 networks. This ensures fast, secure, and borderless transactions with minimal fees.',
  },
  {
    category: 'Booking & Payments',
    question: 'Is my payment secure?',
    answer:
      'Yes, all payments are processed through secure blockchain technology with encryption. We never store your wallet credentials, and all transactions are verified on the blockchain.',
  },
  {
    category: 'Booking & Payments',
    question: 'Can I get a refund?',
    answer:
      'Refund eligibility depends on the event terms and timing. Generally, cancellations made 30+ days before the event are eligible for partial refunds. Contact our support team for specific cases.',
  },

  // VIP Packages
  {
    category: 'VIP Packages',
    question: 'What\'s the difference between Diamond, Platinum, and Gold packages?',
    answer:
      'Diamond offers the ultimate luxury experience with front-row seats, backstage meet & greet, premium dining, and dedicated concierge. Platinum provides premium seating, VIP lounge access, and gourmet dining. Gold includes enhanced seating and select VIP perks. Each tier is detailed on the event page.',
  },
  {
    category: 'VIP Packages',
    question: 'How many guests can I bring with my package?',
    answer:
      'Most packages allow up to 2 guests per booking. The exact number is specified for each package tier on the event detail page. You can book multiple packages for larger groups.',
  },
  {
    category: 'VIP Packages',
    question: 'Are meet & greet opportunities guaranteed?',
    answer:
      'Meet & greet access is included in Diamond and select Platinum packages. While we coordinate with artist management, availability is subject to artist schedule and confirmed closer to the event date. You\'ll be notified of any changes.',
  },
  {
    category: 'VIP Packages',
    question: 'What does VIP lounge access include?',
    answer:
      'VIP lounges feature premium bar service, comfortable seating, air conditioning, private restrooms, and charging stations. Lounge access begins 2 hours before showtime and continues through intermission.',
  },

  // Event Details
  {
    category: 'Event Details',
    question: 'When will I receive my tickets?',
    answer:
      'Digital tickets are sent instantly to your email upon booking confirmation. Physical credentials (if applicable) and detailed venue instructions are sent 24-48 hours before the event.',
  },
  {
    category: 'Event Details',
    question: 'What time should I arrive?',
    answer:
      'VIP guests should arrive 2 hours before showtime to enjoy pre-event amenities, dining, and lounge access. Gates open for VIP entry 30 minutes before general admission.',
  },
  {
    category: 'Event Details',
    question: 'Is parking included?',
    answer:
      'Premium and complimentary parking is included with Diamond and most Platinum packages. The exact parking location and instructions will be provided with your event confirmation.',
  },
  {
    category: 'Event Details',
    question: 'Can I take photos during the event?',
    answer:
      'Personal photography is allowed in most areas, but professional cameras and recording equipment are prohibited. Meet & greet sessions include professional photo opportunities. Specific rules vary by artist.',
  },

  // Account & Support
  {
    category: 'Account & Support',
    question: 'How do I access my bookings?',
    answer:
      'Log in to your account and navigate to "My Bookings" to view all your upcoming and past events. You can download tickets, view details, and manage your bookings from this dashboard.',
  },
  {
    category: 'Account & Support',
    question: 'What if I need to change my booking details?',
    answer:
      'Contact our support team at least 14 days before the event for name changes or guest modifications. Some changes may incur fees depending on the package terms.',
  },
  {
    category: 'Account & Support',
    question: 'How can I contact customer support?',
    answer:
      'Reach us via the contact form on our website, email support@tickettoluxury.com, or WhatsApp at +880 1234-567890. Our support team is available 24/7 for urgent inquiries.',
  },
  {
    category: 'Account & Support',
    question: 'Do you offer group bookings or corporate packages?',
    answer:
      'Yes! For groups of 10+ or corporate bookings, contact our VIP sales team for custom packages, bulk pricing, and exclusive experiences tailored to your needs.',
  },
];

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(faqs.map((faq) => faq.category)))];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <HelpCircle className="mx-auto mb-6 h-16 w-16 text-[#D4AF37]" />
          <h1 className="mb-4 font-serif text-5xl font-bold text-white">
            Frequently Asked{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              Questions
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-400">
            Find answers to common questions about VIP bookings, payments, and event experiences
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#D4AF37]" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 py-4 pl-12 pr-4 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#B76E79] text-black'
                  : 'border border-[#D4AF37]/30 text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.length === 0 ? (
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-12 text-center backdrop-blur-md">
              <p className="text-gray-400">No questions found matching your search.</p>
            </div>
          ) : (
            filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-md transition-all duration-300 hover:border-[#D4AF37]/50"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="flex w-full items-center justify-between p-6 text-left transition-all duration-300"
                >
                  <div className="flex-1 pr-4">
                    <p className="mb-1 text-xs font-semibold text-[#D4AF37]">{faq.category}</p>
                    <h3 className="font-semibold text-white">{faq.question}</h3>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 text-[#D4AF37] transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="border-t border-[#D4AF37]/20 px-6 pb-6 pt-4">
                    <p className="leading-relaxed text-gray-300">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Still Have Questions CTA */}
        <div className="mt-16 rounded-xl border border-[#D4AF37]/20 bg-gradient-to-r from-black/80 to-black/60 p-8 text-center backdrop-blur-md">
          <h3 className="mb-4 font-serif text-2xl font-bold text-white">Still Have Questions?</h3>
          <p className="mb-6 text-gray-300">
            Our support team is here to help. Get in touch and we'll respond within 24 hours.
          </p>
          <Link
            href="/contact"
            className="inline-block rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-8 py-3 font-bold text-black transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
}
